/**
 * E018-S012-T003: AutomationDiscoveryService
 *
 * Finds all Automation blocks across the workspace.
 * Extends BlockDiscoveryService for shared workspace-wide scanning logic.
 *
 * Used by:
 * - WorkspaceAutomationsPanel (E018-S011)
 * - Ghost node creation for cross-doc automations
 * - Relation integrity validation
 */

import { I18n } from '@affine/i18n';
import type { Block } from '@blocksuite/affine/store';
import { resolveAutomationName } from '@blocksuite/affine-block-streamlain';
import { LiveData } from '@toeverything/infra';
import { switchMap } from 'rxjs';

import type { DocRecord, DocsService } from '../../doc';
import type { GuardService } from '../../permissions';
import {
  BlockDiscoveryService,
  type BlockInfo,
  type TrashcanItem,
} from './block-discovery';

function tAutomationDiscovery(key: string, fallback: string): string {
  const translate = (I18n as unknown as Record<string, () => string>)[key];
  if (typeof translate !== 'function') {
    return fallback;
  }
  const translated = translate();
  return translated && translated !== key ? translated : fallback;
}

/**
 * Info about a found Automation block.
 */
export interface AutomationInfo extends BlockInfo {
  /** Automation name */
  name: string;
  /** Source database block ID */
  sourceTableId: string | null;
  /** Source doc ID (for cross-doc detection) */
  sourceDocId: string | null;
  /** Target database block ID */
  targetTableId: string | null;
  /** Target doc ID (for cross-doc detection) */
  targetDocId: string | null;
  /** All configured input database block IDs, including secondary inputs */
  inputTableIds: string[];
  /** All configured output database block IDs */
  outputTableIds: string[];
  /** Workflow ID */
  workflowId: string | null;
  /** S020-8.20.6: Status tracking column ID (for conflict detection) */
  statusTrackingColumnId: string | null;
  /** S020-8.20.6: Output column IDs (for conflict detection) */
  outputColumnIds: string[];
}

/**
 * S020-8.20.6: Column conflict information
 */
export interface ColumnConflict {
  /** The column ID that has a conflict */
  columnId: string;
  /** Type of conflict */
  conflictType: 'status' | 'output';
  /** The automation that owns this column */
  conflictingAutomationId: string;
  /** Name of the conflicting automation */
  conflictingAutomationName: string;
  /** Doc ID of the conflicting automation */
  conflictingDocId: string;
  /** Doc title of the conflicting automation */
  conflictingDocTitle: string;
}

/**
 * Automation-specific doc group.
 */
export interface AutomationDocGroup {
  docId: string;
  docTitle: string;
  automations: AutomationInfo[];
}

type IncomingAutomationAccessState = 'read' | 'restricted';

/**
 * Service for discovering all Automation blocks across the workspace.
 */
export class AutomationDiscoveryService extends BlockDiscoveryService<AutomationInfo> {
  readonly accessibleAutomations$: LiveData<AutomationInfo[]>;

  constructor(
    docsService: DocsService,
    private readonly guardService?: GuardService
  ) {
    super(docsService);
    this.accessibleAutomations$ = LiveData.from<AutomationInfo[]>(
      this.blocks$.pipe(
        switchMap(async (automations: AutomationInfo[]) => {
          if (!this.guardService || automations.length === 0) {
            return automations;
          }

          const docIds = [...new Set(automations.map(a => a.docId))];
          const permissions = await Promise.all(
            docIds.map(async docId => ({
              docId,
              canRead: await this.guardService!.can('Doc_Read', docId),
            }))
          );
          const readableDocs = new Set(
            permissions
              .filter(permission => permission.canRead)
              .map(permission => permission.docId)
          );

          return automations.filter(automation =>
            readableDocs.has(automation.docId)
          );
        })
      ),
      []
    );
  }

  // ============================================================
  // Abstract Properties Implementation
  // ============================================================

  readonly blockFlavour = 'affine:automation';
  readonly trashcanBlockType = 'automation';

  // ============================================================
  // Abstract Methods Implementation
  // ============================================================

  extractBlockInfo(block: Block, docRecord: DocRecord): AutomationInfo | null {
    const props = block.model?.props as
      | {
          title?: { toString(): string };
          titleMode?: 'auto' | 'custom';
          sourceTableId?: string | null;
          sourceDocId?: string | null;
          targetTableId?: string | null;
          targetDocId?: string | null;
          inputTables?: TableReferenceCollection;
          outputTables?: TableReferenceCollection;
          workflowId?: string | null;
          workflowName?: string | null;
          xywh?: string;
          // S020-8.20.6: Status tracking and output mappings for conflict detection
          statusTracking?: {
            enabled?: boolean;
            columnId?: string | null;
          };
          outputMappings?:
            | Array<{ targetColumnId?: string }>
            | { toArray?: () => Array<{ targetColumnId?: string }> };
        }
      | undefined;

    const name = resolveAutomationName(
      {
        title: props?.title,
        titleMode: props?.titleMode,
        workflowName: props?.workflowName,
      },
      tAutomationDiscovery(
        'com.streamlain.streamlain.common.new-automation',
        'New Automation'
      )
    );

    // S020-8.20.6: Extract status tracking column ID
    const statusTrackingColumnId =
      props?.statusTracking?.enabled && props?.statusTracking?.columnId
        ? props.statusTracking.columnId
        : null;

    // S020-8.20.6: Extract output column IDs (handle YArray)
    const outputColumnIds = toReadonlyArray(props?.outputMappings)
      .map((m: { targetColumnId?: string }) => m.targetColumnId)
      .filter((id): id is string => !!id);

    return {
      blockId: block.model?.id ?? block.id,
      docId: docRecord.id,
      docTitle: docRecord.title$.value || 'Untitled',
      name,
      sourceTableId: props?.sourceTableId ?? null,
      sourceDocId: props?.sourceDocId ?? null,
      targetTableId: props?.targetTableId ?? null,
      targetDocId: props?.targetDocId ?? null,
      inputTableIds: getAutomationReferencedTableIds(
        props?.sourceTableId ?? null,
        props?.inputTables
      ),
      outputTableIds: getAutomationReferencedTableIds(
        props?.targetTableId ?? null,
        props?.outputTables
      ),
      workflowId: props?.workflowId ?? null,
      xywh: props?.xywh,
      isInTrash: false,
      statusTrackingColumnId,
      outputColumnIds,
    };
  }

  override extractTrashcanInfo(
    item: TrashcanItem,
    docRecord: DocRecord
  ): AutomationInfo | null {
    return {
      blockId: item.id,
      docId: docRecord.id,
      docTitle: docRecord.title$.value || 'Untitled',
      name: item.displayName || 'Deleted Automation',
      sourceTableId: null,
      sourceDocId: null,
      targetTableId: null,
      targetDocId: null,
      inputTableIds: [],
      outputTableIds: [],
      workflowId: null,
      xywh: undefined,
      isInTrash: true,
      // S020-8.20.6: Trashed automations have no column conflicts
      statusTrackingColumnId: null,
      outputColumnIds: [],
    };
  }

  // ============================================================
  // Convenience Aliases
  // ============================================================

  /**
   * Alias for blocks$ - all automations.
   */
  get automations$(): LiveData<AutomationInfo[]> {
    return this.blocks$;
  }

  /**
   * Alias for canvasBlocks$ - canvas-only automations.
   */
  get canvasAutomations$(): LiveData<AutomationInfo[]> {
    return this.canvasBlocks$;
  }

  /**
   * Automations grouped by doc with `automations` field.
   */
  get automationsByDoc$(): LiveData<AutomationDocGroup[]> {
    return this.blocksByDoc$.map(groups =>
      groups.map(
        (g): AutomationDocGroup => ({
          docId: g.docId,
          docTitle: g.docTitle,
          automations: g.blocks,
        })
      )
    );
  }

  /**
   * Canvas automations grouped by doc.
   */
  get canvasAutomationsByDoc$(): LiveData<AutomationDocGroup[]> {
    return this.canvasBlocksByDoc$.map(groups =>
      groups.map(
        (g): AutomationDocGroup => ({
          docId: g.docId,
          docTitle: g.docTitle,
          automations: g.blocks,
        })
      )
    );
  }

  // ============================================================
  // E018-S016: Workspace Trashcan
  // ============================================================

  /**
   * E018-S016: All trashed automations across workspace.
   */
  get trashcanAutomations$(): LiveData<AutomationInfo[]> {
    return this.blocks$.map((automations: AutomationInfo[]) =>
      automations.filter(a => a.isInTrash === true)
    );
  }

  /**
   * E018-S016: Trashed automations grouped by doc.
   */
  get trashcanAutomationsByDoc$(): LiveData<AutomationDocGroup[]> {
    return this.trashcanAutomations$.map((automations: AutomationInfo[]) => {
      const byDoc = new Map<
        string,
        { docTitle: string; automations: AutomationInfo[] }
      >();

      for (const a of automations) {
        if (!byDoc.has(a.docId)) {
          byDoc.set(a.docId, {
            docTitle: a.docTitle,
            automations: [],
          });
        }
        byDoc.get(a.docId)!.automations.push(a);
      }

      return Array.from(byDoc.entries()).map(([docId, data]) => ({
        docId,
        docTitle: data.docTitle,
        automations: data.automations,
      }));
    });
  }

  // ============================================================
  // Automation-specific Methods
  // ============================================================

  /**
   * Find an automation by blockId.
   */
  findAutomation(blockId: string): AutomationInfo | undefined {
    return this.findBlock(blockId);
  }

  /**
   * Watch a specific automation reactively.
   */
  watchAutomation(blockId: string): LiveData<AutomationInfo | undefined> {
    return this.blocks$.map((automations: AutomationInfo[]) =>
      automations.find(a => a.blockId === blockId)
    );
  }

  /**
   * Find all automations that reference a given database as input or output.
   * Used after database move to update automation references.
   */
  findAutomationsReferencingDatabase(
    databaseBlockId: string
  ): Array<{ docId: string; blockId: string }> {
    return this.blocks$.value
      .filter(a => automationReferencesDatabase(a, databaseBlockId))
      .map(a => ({ docId: a.docId, blockId: a.blockId }));
  }

  /**
   * Find all automations from OTHER docs that reference a given database.
   * Used to create "incoming" ghost nodes for automations (bidirectional).
   *
   * @param targetDatabaseId - The database block ID to find incoming automations for
   * @param currentDocId - The current doc ID to exclude (we only want cross-doc automations)
   */
  findIncomingAutomations(
    targetDatabaseId: string,
    currentDocId: string
  ): Array<{
    automationId: string;
    automationName: string;
    docId: string;
    docTitle: string;
    accessState: IncomingAutomationAccessState;
    direction: 'source' | 'target';
  }> {
    const result: Array<{
      automationId: string;
      automationName: string;
      docId: string;
      docTitle: string;
      accessState: IncomingAutomationAccessState;
      direction: 'source' | 'target';
    }> = [];
    const readableAutomationKeys = new Set(
      (this.accessibleAutomations$?.value ?? this.blocks$.value).map(
        automation => `${automation.docId}:${automation.blockId}`
      )
    );

    for (const automation of this.blocks$.value) {
      // Skip automations in current doc (we only want cross-doc)
      if (automation.docId === currentDocId) continue;
      // Skip trashed automations
      if (automation.isInTrash) continue;
      const accessState: IncomingAutomationAccessState =
        readableAutomationKeys.has(`${automation.docId}:${automation.blockId}`)
          ? 'read'
          : 'restricted';
      const automationName =
        accessState === 'read'
          ? automation.name
          : tAutomationDiscovery(
              'com.streamlain.streamlain.ghost.restricted-automation',
              'Restricted automation'
            );
      const docTitle =
        accessState === 'read'
          ? automation.docTitle
          : tAutomationDiscovery(
              'com.streamlain.streamlain.ghost.restricted-document',
              'Restricted document'
            );

      if (automationUsesDatabaseAsInput(automation, targetDatabaseId)) {
        result.push({
          automationId: automation.blockId,
          automationName,
          docId: automation.docId,
          docTitle,
          accessState,
          direction: 'source',
        });
      }

      if (automationUsesDatabaseAsOutput(automation, targetDatabaseId)) {
        result.push({
          automationId: automation.blockId,
          automationName,
          docId: automation.docId,
          docTitle,
          accessState,
          direction: 'target',
        });
      }
    }

    return result;
  }

  /**
   * Find TRASHED automations in the SAME doc that reference a given database.
   * Used to create ghost nodes for deleted automations that had connections.
   *
   * @param targetDatabaseId - The database block ID to find trashed automations for
   * @param currentDocId - The current doc ID (we only want same-doc automations)
   */
  findTrashedLocalAutomations(
    targetDatabaseId: string,
    currentDocId: string
  ): Array<{
    automationId: string;
    automationName: string;
    docId: string;
    docTitle: string;
    direction: 'source' | 'target';
  }> {
    const result: Array<{
      automationId: string;
      automationName: string;
      docId: string;
      docTitle: string;
      direction: 'source' | 'target';
    }> = [];

    for (const automation of this.blocks$.value) {
      // Only include automations in current doc
      if (automation.docId !== currentDocId) continue;
      // Only include trashed automations
      if (!automation.isInTrash) continue;

      if (automationUsesDatabaseAsInput(automation, targetDatabaseId)) {
        result.push({
          automationId: automation.blockId,
          automationName: automation.name,
          docId: automation.docId,
          docTitle: automation.docTitle,
          direction: 'source',
        });
      }

      if (automationUsesDatabaseAsOutput(automation, targetDatabaseId)) {
        result.push({
          automationId: automation.blockId,
          automationName: automation.name,
          docId: automation.docId,
          docTitle: automation.docTitle,
          direction: 'target',
        });
      }
    }

    return result;
  }

  // ============================================================
  // S020-8.20.6: Column Conflict Detection
  // ============================================================

  /**
   * S020-8.20.6: Find column conflicts for a given automation.
   *
   * Checks if any columns the automation wants to use are already
   * used by other automations (status tracking or output columns).
   *
   * @param currentAutomationId - The automation ID to check (excluded from results)
   * @param tableId - The table ID to check columns in
   * @param statusColumnId - Status column ID to check (or null)
   * @param outputColumnIds - Output column IDs to check
   * @returns Array of conflicts found
   */
  findColumnConflicts(
    currentAutomationId: string,
    tableId: string,
    statusColumnId: string | null,
    outputColumnIds: string[]
  ): ColumnConflict[] {
    const conflicts: ColumnConflict[] = [];

    for (const automation of this.blocks$.value) {
      // Skip the current automation
      if (automation.blockId === currentAutomationId) continue;
      // Skip trashed automations
      if (automation.isInTrash) continue;
      // Skip automations that don't use this table
      if (!automationReferencesDatabase(automation, tableId)) {
        continue;
      }

      // Check status column conflict
      if (
        statusColumnId &&
        automation.statusTrackingColumnId === statusColumnId
      ) {
        conflicts.push({
          columnId: statusColumnId,
          conflictType: 'status',
          conflictingAutomationId: automation.blockId,
          conflictingAutomationName: automation.name,
          conflictingDocId: automation.docId,
          conflictingDocTitle: automation.docTitle,
        });
      }

      // Check output column conflicts
      for (const outputColumnId of outputColumnIds) {
        if (automation.outputColumnIds.includes(outputColumnId)) {
          conflicts.push({
            columnId: outputColumnId,
            conflictType: 'output',
            conflictingAutomationId: automation.blockId,
            conflictingAutomationName: automation.name,
            conflictingDocId: automation.docId,
            conflictingDocTitle: automation.docTitle,
          });
        }
        // Also check if other automation uses this as status column
        if (automation.statusTrackingColumnId === outputColumnId) {
          conflicts.push({
            columnId: outputColumnId,
            conflictType: 'status',
            conflictingAutomationId: automation.blockId,
            conflictingAutomationName: automation.name,
            conflictingDocId: automation.docId,
            conflictingDocTitle: automation.docTitle,
          });
        }
      }

      // Check if our status column is used as output by another automation
      if (
        statusColumnId &&
        automation.outputColumnIds.includes(statusColumnId)
      ) {
        // Only add if not already added as status conflict
        const alreadyAdded = conflicts.some(
          c =>
            c.columnId === statusColumnId &&
            c.conflictingAutomationId === automation.blockId
        );
        if (!alreadyAdded) {
          conflicts.push({
            columnId: statusColumnId,
            conflictType: 'output',
            conflictingAutomationId: automation.blockId,
            conflictingAutomationName: automation.name,
            conflictingDocId: automation.docId,
            conflictingDocTitle: automation.docTitle,
          });
        }
      }
    }

    return conflicts;
  }

  /**
   * S020-8.20.6: Get all columns used by other automations for a given table.
   *
   * Returns a map of columnId -> conflict info for quick lookup in UI.
   *
   * @param currentAutomationId - The automation ID to exclude
   * @param tableId - The table ID to check
   * @returns Map of columnId to conflict info
   */
  getUsedColumns(
    currentAutomationId: string,
    tableId: string
  ): Map<string, ColumnConflict> {
    const usedColumns = new Map<string, ColumnConflict>();

    for (const automation of this.blocks$.value) {
      // Skip the current automation
      if (automation.blockId === currentAutomationId) continue;
      // Skip trashed automations
      if (automation.isInTrash) continue;
      // Skip automations that don't use this table
      if (!automationReferencesDatabase(automation, tableId)) {
        continue;
      }

      // Add status column
      if (automation.statusTrackingColumnId) {
        usedColumns.set(automation.statusTrackingColumnId, {
          columnId: automation.statusTrackingColumnId,
          conflictType: 'status',
          conflictingAutomationId: automation.blockId,
          conflictingAutomationName: automation.name,
          conflictingDocId: automation.docId,
          conflictingDocTitle: automation.docTitle,
        });
      }

      // Add output columns
      for (const columnId of automation.outputColumnIds) {
        if (!usedColumns.has(columnId)) {
          usedColumns.set(columnId, {
            columnId,
            conflictType: 'output',
            conflictingAutomationId: automation.blockId,
            conflictingAutomationName: automation.name,
            conflictingDocId: automation.docId,
            conflictingDocTitle: automation.docTitle,
          });
        }
      }
    }

    return usedColumns;
  }

  // ============================================================
  // sl-trigger-lifecycle: Removed Automation Detection
  // ============================================================
  // StreamLain: removedAutomations$ commented out - unused and has type errors
  // TODO: Re-enable when backend cleanup is implemented
  // /**
  //  * sl-trigger-lifecycle: Track removed automations for backend cleanup.
  //  *
  //  * Returns a LiveData that emits info about automations that were removed
  //  * since the last emission. Used for backend config cleanup.
  //  */
  // get removedAutomations$(): LiveData<
  //   Array<{
  //     blockId: string;
  //     workspaceId: string;
  //     docId: string;
  //   }>
  // > {
  //   // Track previous blockIds
  //   let previousBlockIds = new Set<string>();
  //   let previousBlocksMap = new Map<
  //     string,
  //     { workspaceId: string; docId: string }
  //   >();
  //
  //   return this.blocks$.map((automations: AutomationInfo[]) => {
  //     const currentBlockIds = new Set(automations.map(a => a.blockId));
  //     const currentBlocksMap = new Map(
  //       automations.map(a => [
  //         a.blockId,
  //         { workspaceId: a.workspaceId, docId: a.docId },
  //       ])
  //     );
  //
  //     // Find removed blockIds
  //     const removed: Array<{
  //       blockId: string;
  //       workspaceId: string;
  //       docId: string;
  //     }> = [];
  //
  //     for (const blockId of previousBlockIds) {
  //       if (!currentBlockIds.has(blockId)) {
  //         const info = previousBlocksMap.get(blockId);
  //         if (info) {
  //           removed.push({
  //             blockId,
  //             workspaceId: info.workspaceId,
  //             docId: info.docId,
  //           });
  //         }
  //       }
  //     }
  //
  //     // Update previous state
  //     previousBlockIds = currentBlockIds;
  //     previousBlocksMap = currentBlocksMap;
  //
  //     return removed;
  //   });
  // }
}

type TableReference = { tableId?: string | null };
type TableReferenceCollection =
  | readonly TableReference[]
  | { toArray?: () => readonly TableReference[] };

export function getAutomationReferencedTableIds(
  primaryTableId: string | null | undefined,
  tables: TableReferenceCollection | undefined
): string[] {
  const ids = new Set<string>();
  if (primaryTableId) {
    ids.add(primaryTableId);
  }
  for (const table of toReadonlyArray(tables)) {
    if (table.tableId) {
      ids.add(table.tableId);
    }
  }
  return Array.from(ids);
}

function automationReferencesDatabase(
  automation: AutomationInfo,
  databaseBlockId: string
): boolean {
  return (
    automationUsesDatabaseAsInput(automation, databaseBlockId) ||
    automationUsesDatabaseAsOutput(automation, databaseBlockId)
  );
}

function automationUsesDatabaseAsInput(
  automation: AutomationInfo,
  databaseBlockId: string
): boolean {
  return automation.inputTableIds.includes(databaseBlockId);
}

function automationUsesDatabaseAsOutput(
  automation: AutomationInfo,
  databaseBlockId: string
): boolean {
  return automation.outputTableIds.includes(databaseBlockId);
}

function toReadonlyArray<T>(
  collection: readonly T[] | { toArray?: () => readonly T[] } | undefined
): readonly T[] {
  if (!collection) {
    return [];
  }
  if (Array.isArray(collection)) {
    return collection;
  }
  const array =
    'toArray' in collection && typeof collection.toArray === 'function'
      ? collection.toArray()
      : [];
  return Array.isArray(array) ? array : [];
}
